import { useEffect, useState } from "react";
import { query } from "./dataClient.js";
import { sumSeries } from "./renderers.js";
import { candidateDimsFor } from "./compositionModel.js";
import { DIMENSIONS, encodeScopePath } from "./dimensionDefs.js";

// useDiscriminatingDescent — auto-drills the HIGHEST-VOLUME path from a scope,
// greedily. At each level it breaks the current scope down by each candidate
// dimension (scope-side only — no org baseline), picks the dimension whose top
// member captures the largest share of the scope's prompt volume, drills into
// that member, and repeats — building the projected descent as a stack of levels
// (the Position spike chart continues itself down the fattest branch). Stops when
// no candidate's top member clears the dominant-share floor, dims run out, or the
// depth cap is hit.
//
// (Formerly a Jensen-Shannon "most-distinctive vs org" walk. That needed a second
// org-baseline breakdown per candidate — half the reads — and picked the most
// *atypical* dim; this follows the biggest chunk instead, which is both cheaper
// and a clearer story for a "where you are" chart. The JSD engine still drives
// the Composition column order via useScopeDistinctiveness — that's untouched.)
//
// The walk is inherently sequential (each level's scope depends on the last), so
// it runs as an aborting async effect over the imperative query() (cache:true,
// so the top level reuses whatever the ranking/composition already fetched). The
// per-level pick (pickVolumeStep) and the whole walk (walkDescent) are pure,
// query-injected, and unit-tested in useDiscriminatingDescent.test.js.

const MAX_DEPTH = 4;         // auto-drilled levels beyond the committed scope
const MAX_CANDIDATES = 6;    // dims profiled per level
const SCOPE_K = 8;           // top members fetched per candidate breakdown
// Stop drilling once even the fattest member is below this share of the scope —
// there's no dominant region left to follow, so it would be chasing noise.
// (The volume-drill analogue of the old DISTINCT_MIN divergence floor.)
const VOLUME_MIN_SHARE = 0.1;

/**
 * Choose the level to drill from the volume-scored candidate dims. Returns
 * { best, pick } or null to STOP (no scorable candidate, or the fattest member
 * is below the dominant-share floor).
 *
 * @param {{scored:Array<{dim,entries,topActivity,distinct}>, curTotal:number}} args
 */
export function pickVolumeStep({ scored, curTotal }) {
  const ranked = (scored || [])
    .filter((s) => s.entries && s.entries.length)
    .sort((a, b) => (b.topActivity - a.topActivity) || String(a.dim).localeCompare(String(b.dim)));
  const best = ranked[0];
  if (!best) return null;
  // No dominant chunk left → stop.
  if (curTotal > 0 && best.topActivity / curTotal < VOLUME_MIN_SHARE) return null;
  const pick = best.entries[0]; // the top member by volume
  if (!pick) return null;
  return { best, pick };
}

/**
 * Walk the greedy highest-volume descent from `path`. Pure aside from the
 * injected `query`; checks `signal.cancelled` between awaits and returns
 * whatever it has so far.
 *
 * @param {{path:Array, range:*, query:Function, signal?:{cancelled:boolean}}} args
 * @returns {Promise<Array>} the projected levels
 */
export async function walkDescent({ path, range, query: q, signal }) {
  const bundleTotal = async (scopePath) => {
    const b = await q("getOverviewActivityBundle", { scopePath, bucketCount: 1, topN: 1, range }, { cache: true });
    return sumSeries(b?.prompts?.total);
  };

  const levels = [];
  let cur = path;
  const used = new Set(path.map((s) => s.dim));

  for (let depth = 0; depth < MAX_DEPTH; depth++) {
    const cands = candidateDimsFor(cur, MAX_CANDIDATES).filter((d) => !used.has(d));
    if (!cands.length) break;

    // Fire the scope-total bundle and every candidate breakdown in ONE parallel
    // wave. The candidates don't need curTotal to run — it only gates the share
    // floor afterward — so awaiting the bundle first just adds a round-trip per
    // level (and the walk is already sequential across levels). A single dim's
    // fetch failing must not abort the walk: treat it as empty so it scores 0.
    const [curTotal, scored] = await Promise.all([
      bundleTotal(cur),
      Promise.all(cands.map(async (dim) => {
        const data = await q(
          "getDimensionEntries",
          { dimKey: dim, parentScopePath: cur, limit: SCOPE_K, range, sparkline: false },
          { cache: true },
        ).catch(() => null);
        const entries = data?.entries || [];
        return { dim, entries, topActivity: Math.max(0, Number(entries[0]?.activity) || 0), distinct: data?.total ?? entries.length };
      })),
    ]);
    if (signal?.cancelled) return levels;
    if (!(curTotal > 0)) break;

    const picked = pickVolumeStep({ scored, curTotal });
    if (!picked) break;
    const { best, pick } = picked;

    const selectedId = String(pick.id);
    // members keep getDimensionEntries' activity-descending order (the ranked
    // display order); the pick is entries[0], the fattest member.
    const members = best.entries.map((e) => ({ id: String(e.id), label: e.label, value: Math.max(0, Number(e.activity) || 0) }));

    levels.push({
      dim: best.dim,
      dimLabel: DIMENSIONS[best.dim]?.label || best.dim,
      prefix: cur,
      members,
      selectedId,
      selectedLabel: pick.label,
      distinct: best.distinct,
      share: curTotal > 0 ? best.topActivity / curTotal : 0,
    });

    cur = [...cur, { dim: best.dim, ids: [selectedId], id: selectedId }];
    used.add(best.dim);
  }
  return levels;
}

export function useDiscriminatingDescent(scopePath, range) {
  const path = scopePath || [];
  // Runs at EVERY scope including the global root ([]) — the volume walk needs
  // no special root seed (unlike the old JSD walk, which had no signal at root).
  const key = encodeScopePath(path);
  const [state, setState] = useState({ descent: [], loading: false });

  useEffect(() => {
    const signal = { cancelled: false };
    setState((s) => ({ descent: s.descent, loading: true }));
    walkDescent({ path, range, query, signal })
      .then((levels) => { if (!signal.cancelled) setState({ descent: levels, loading: false }); })
      .catch((e) => {
        if (signal.cancelled) return;
        console.error("[useDiscriminatingDescent]", e);
        setState({ descent: [], loading: false });
      });
    return () => { signal.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, range]);

  return state;
}
